import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

const JOTFORM_ID = '260563866922062';
const INVESTOR_EMAIL = 'investor.relations@yournest.in';

// Co-investor logos – display order is the array order below (no sorting).
const CO_INVESTOR_LOGOS = [
  { name: 'Sequoia', src: '/images/co-investors/sequoia.png' },
  { name: 'Lightspeed (includes Lightspeed Ventures)', src: '/images/co-investors/lightspeed.png' },
  { name: 'Google Ventures (GV)', src: '/images/co-investors/google_ventures.png' },
  { name: 'Enterprise Singapore', src: '/images/co-investors/enterprise_singapore.png' },
  { name: 'SPRING Singapore', src: '/images/co-investors/spring_singapore.png' },
  { name: 'HDFC Capital', src: '/images/co-investors/hdfc_capital.png' },
  { name: 'RTP Global', src: '/images/co-investors/rtp_global.png' },
  { name: 'Michael & Susan Dell Foundation', src: '/images/co-investors/michael_susan_dell_foundation.png' },
  { name: 'Beenext', src: '/images/co-investors/beenext.png' },
  { name: 'Jungle Ventures', src: '/images/co-investors/jungle_ventures.png' },
  { name: 'Rakuten Capital', src: '/images/co-investors/rakuten_capital.png' },
  { name: 'Chiratae', src: '/images/co-investors/chiratae.png' },
  { name: 'Accion', src: '/images/co-investors/accion.png' },
  { name: 'Jafco Asia', src: '/images/co-investors/jafco_asia.png' },
  { name: 'Omidyar Network India', src: '/images/co-investors/omidyar_network_india.png' },
  { name: 'Ivycap Ventures', src: '/images/co-investors/ivycap_ventures.png' },
  { name: '3one4 Capital', src: '/images/co-investors/3one4_capital.png' },
  { name: 'Stride Ventures', src: '/images/co-investors/stride_ventures.png' },
  { name: 'Innoven Capital', src: '/images/co-investors/innoven_capital.png' },
  { name: 'Tembusu Partners', src: '/images/co-investors/tembusu_partners.png' },
  { name: 'Prime Venture Partners', src: '/images/co-investors/prime_venture_partners.png' },
  { name: 'Airtel', src: '/images/co-investors/airtel.png' },
  { name: 'UST Global', src: '/images/co-investors/ust_global.png' },
  { name: 'Indian Angel Network', src: '/images/co-investors/indian_angel_network.png' },
  { name: 'Venture Catalysts++', src: '/images/co-investors/venture_catalysts.png' },
  { name: 'Inventus Capital Partners', src: '/images/co-investors/inventus_capital_partners.png' },
  { name: 'Parampara', src: '/images/co-investors/parampara.png' },
  { name: 'Exfinity', src: '/images/co-investors/exfinity.png' },
  { name: 'Lumis', src: '/images/co-investors/lumis.png' },
  { name: 'Grow X Ventures', src: '/images/co-investors/grow_x_ventures.png' },
  { name: 'Ideaspring', src: '/images/co-investors/ideaspring.png' },
  { name: 'Siana Capital', src: '/images/co-investors/siana_capital.png' },
  { name: 'Refex', src: '/images/co-investors/refex.png' },
  { name: 'Turbostart', src: '/images/co-investors/turbostart.png' },
  { name: 'Kaaj Ventures', src: '/images/co-investors/kaaj_ventures.png' },
  { name: 'Founder Bank Capital', src: '/images/co-investors/founder_bank_capital.png' },
  { name: 'Kitaki Ventures', src: '/images/co-investors/kitaki_ventures.png' },
  { name: 'Vinod Dham', src: '/images/co-investors/vinod_dham.png' },
];

const InvestorsPage = () => {
  usePageTitle('Investors');
  const logoGalleryRef = useRef(null);
  const [showInvestorForm, setShowInvestorForm] = useState(false);

  // Preload all co-investor images to avoid lag when scrolling the carousel
  useEffect(() => {
    CO_INVESTOR_LOGOS.forEach((logo) => {
      const img = new Image();
      img.src = `${logo.src}?v=2`;
    });
  }, []);

  // Infinite scroll: duplicate list and wrap scroll position when reaching the end
  const infiniteLogos = useMemo(() => [...CO_INVESTOR_LOGOS, ...CO_INVESTOR_LOGOS], []);
  const onGalleryScroll = useRef(() => {});
  useEffect(() => {
    onGalleryScroll.current = () => {
      const el = logoGalleryRef.current;
      if (!el) return;
      const half = el.scrollWidth / 2;
      if (el.scrollLeft >= half - 1) el.scrollLeft -= half;
    };
  }, []);
  useEffect(() => {
    const el = logoGalleryRef.current;
    if (!el) return;
    const handler = () => onGalleryScroll.current();
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, []);

  const scrollGallery = (direction) => {
    const el = logoGalleryRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    const half = el.scrollWidth / 2;
    if (direction === 'left' && el.scrollLeft <= 0 && half > 0) {
      el.scrollLeft = half - amount;
      return;
    }
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>

            <h1 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-4">
              The <span className="text-[#C8473A]">Transfer of Trust</span>
            </h1>
            <p className="text-lg text-gray-600">
              We adopt a three-pronged approach towards investing that focuses on delivering superior returns, mitigating risks and executing healthy exits. Every investment decision is based on the 'Transfer of Trust' principle: the faith reposed in us by our investors is backed by confidence we place in founders as we become partners in their journey.
            </p>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {/* Transfer of Trust image (floated right; wrap text on desktop) */}
            <div className="not-prose w-full md:w-[320px] lg:w-[380px] md:float-right md:ml-8 md:mb-4 md:mt-0 mt-6 mb-4">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-2px_rgba(0,0,0,0.08)] overflow-hidden p-2">
                <div className="relative w-full aspect-[846/823] overflow-hidden rounded-2xl">
                  <img
                    src="/images/transfer-of-trust.png?v=20260225"
                    alt="Transfer of Trust (Credit: GeminiAI)"
                    className="absolute inset-0 w-full h-full object-contain block rounded-2xl"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-8">
              By having a significant minority stake, YourNest retains enough room for future dilution and funding options.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              A prime example of this is <Link to="/portfolio?open=Uniphore" className="text-[#C8473A] font-medium hover:underline">Uniphore</Link>, now a global Unicorn conversational AI player in international markets with a valuation of US$ 2.5 bn. One of our early investments in YourNest Angel Fund in 2014 with an entry valuation of US$ 3.5 mn, its exit in 2020 delivered an enviable DPI of 0.62 at an IRR of 42.7%.
            </p>

            <p className="text-gray-700 leading-relaxed mb-12">
              Similarly, our portfolio companies do not chase fleeting trends; they focus on creating genuine and sustainable value with IPs, making them desirable for companies looking to consolidate markets, acquire cutting-edge technology, or expand their reach; thereby creating an all-round win for our LPs, founders and incoming investors.
            </p>

            <p className="text-gray-700 leading-relaxed mb-12">
              In every exit, we have consistently endeavoured to achieve not just the highest possible return on investment, but also leave incoming investors with adequate potential to deliver a healthy future upside on their own investment. And, for our founders, every exit we make opens a new set of opportunities for them.
            </p>

            <div className="clear-both" />

            <h2 className="text-3xl font-bold text-[#211F1F] mb-6">
              <span className="text-[#C8473A]">Institutional</span> Investors
            </h2>
            <div className="flex flex-col md:flex-row gap-8 md:gap-10 mb-12 items-end">
              <div className="flex-1 min-w-0">
                <p className="text-gray-700 leading-relaxed mb-6">
                  In Fund I, of the US$ 14 mn corpus, 40% participation is by institutional investors led by the Government of India's Ministry of Finance (MoF) via SIDBI. In addition, IIFL Seed Venture FoF and Northgate Capital from Silicon Valley have also participated.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  In Fund II (US$ 30 mn corpus) we earned the trust of existing investors in Fund I who contributed 40% of the corpus. The Sovereign Funds' participation increased to 35%, with the Ministry of Electronics and IT (Meity) participating via Canbank Ventures in addition to MoF. Local and global family offices contributed 27%, and international LPs another 25% of the corpus.
                </p>

                <p className="text-gray-700 leading-relaxed mb-0">
                  NIIF (National Investment & Infrastructure Fund) invested 20% of the US$ 75 mn corpus in YourNest Innovative Products Fund III as its first VC investment, and as our Anchor Investor. MaxLife Insurance too came on board in this fund as an institutional investor along with SIDBI.
                </p>
              </div>
              <div className="flex-shrink-0 w-full md:w-[320px] lg:w-[380px] bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-2px_rgba(0,0,0,0.08)] overflow-hidden p-6 flex items-center justify-center">
                <img
                  src="/images/Institutional_Investors_Logos_090226_Grey_Outline.png"
                  alt="Institutional Investors - SIDBI, 360 ONE, Max Life Insurance, Northgate and others"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

            <h2 className="text-3xl font-bold text-[#211F1F] mb-6">
              <span className="text-[#C8473A]">Co-investors</span>
            </h2>

            <p className="text-gray-700 leading-relaxed mb-12">
              Our portfolio companies have also seen significant follow-on rounds with leading VCs actively participating, thus reaffirming our initial investment decisions.
            </p>
          </motion.div>
        </div>

        {/* Scroll-snap logo gallery (constrained to text width) */}
        <section className="w-full mt-4 pb-16" aria-label="Co-investor logos">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              ref={logoGalleryRef}
              tabIndex={0}
              role="region"
              aria-label="Co-investor logo carousel"
              className="flex gap-8 md:gap-12 overflow-x-auto overflow-y-hidden py-6 scroll-smooth snap-x snap-mandatory focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8473A] focus-visible:ring-offset-2 rounded-lg"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {infiniteLogos.map((logo, index) => (
                <div
                  key={`co-investor-${index}`}
                  className="flex-shrink-0 w-[140px] md:w-[160px] snap-center flex items-center justify-center"
                >
                  <div className="w-full aspect-square max-h-[140px] md:max-h-[160px] bg-white rounded-xl flex items-center justify-center p-4 shadow-sm border border-gray-100">
                    <img
                      src={`${logo.src}?v=2`}
                      alt={logo.name}
                      className="w-full h-full object-contain"
                      loading="eager"
                    />
                  </div>
                </div>
              ))}
            </motion.div>
            <div className="flex justify-center gap-4 mt-4">
              <button
                type="button"
                onClick={() => scrollGallery('left')}
                className="p-2 rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-[#C8473A] hover:text-[#C8473A] transition-colors shadow-sm"
                aria-label="Scroll co-investor logos left"
              >
                <ChevronLeft className="w-6 h-6" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => scrollGallery('right')}
                className="p-2 rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-[#C8473A] hover:text-[#C8473A] transition-colors shadow-sm"
                aria-label="Scroll co-investor logos right"
              >
                <ChevronRight className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>
            <style>{`
              .overflow-x-auto::-webkit-scrollbar { display: none; }
            `}</style>
          </div>
        </section>

        {/* Invest With Us - same horizontal alignment as content above (container padding only) */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" aria-label="Invest With Us">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="py-6 md:py-8">
              <h2 className="text-2xl md:text-3xl font-bold text-[#211F1F] mb-3">
                <span className="text-[#C8473A]">Invest</span> With Us
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-normal">
                Please email us at{' '}
                <a
                  href={`mailto:${INVESTOR_EMAIL}`}
                  className="text-[#C8473A] font-medium hover:underline inline align-baseline"
                >
                  <Mail className="w-4 h-4 inline-block align-middle mr-1.5" aria-hidden />
                  {INVESTOR_EMAIL}
                </a>
                {' '}or{' '}
                <button
                  type="button"
                  className="text-[#C8473A] font-medium hover:underline bg-transparent border-0 p-0 cursor-pointer align-baseline"
                  onClick={() => setShowInvestorForm((v) => !v)}
                >
                  {showInvestorForm ? 'hide the form' : 'fill this form'}
                </button>
                .
              </p>
            </div>
            <div
              className={`${showInvestorForm ? 'border-t border-gray-200 pt-8 mt-2' : 'hidden'}`}
              aria-hidden={!showInvestorForm}
            >
              <iframe
                title="Potential Investor Form"
                src={`${process.env.PUBLIC_URL || ''}/jotform-investor.html`}
                frameBorder="0"
                width="100%"
                height="800"
                className="min-h-[600px] w-full block border-0"
                style={{ border: 0 }}
                allowFullScreen
                allow="geolocation; microphone; camera"
              />
            </div>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default InvestorsPage;